import { Form, Tabs } from "antd";
import PageHeader from "components/PageHeader";
import Spinner from "components/Spinner";
import { useApprovePotentialCustomer } from "hooks/potentialCustomer/mutation";
import { useCreateSeller, useGetSellerDetail, useUpdateSeller } from "hooks/seller";
import useList from "pages/App/Agent/List/useList";
import { COMPANY_TYPE, PAYMENT_METHOD } from "pages/App/Agent/constants";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { joinArray, notify } from "utils/helperFuncs";
import CommonInfo from "../CommonInfo";
import "./index.scss";

const { TabPane } = Tabs;

export const CreateSellerForm = ({ isUpdate, isApproveSeller }) => {
  const [logoUrl, setLogoUrl] = useState("");
  const { id, potentialCustomerId } = useParams();
  const [isCompany, setIsCompany] = useState(true);
  const [form] = Form.useForm();
  const { handleCreateSeller, loading: loadingCreate } = useCreateSeller();
  const { handleApprovePotentialCustomer, loading: loadingApprove } = useApprovePotentialCustomer();
  const { refetch } = useList();
  const { handleUpdateSeller, loading: loadingUpdate } = useUpdateSeller();
  const [activeTab, setActiveTab] = useState("common-info");
  const { data, loading } = useGetSellerDetail({ id });
  const [location, setLocation] = useState({
    cityID: null,
  });
  const history = useHistory();
  const { t } = useTranslation();

  function getLogoUrl(url) {
    setLogoUrl(url);
  }

  function changeTab(tabKey) {
    setActiveTab(tabKey);
  }

  function initForm() {
    if (isUpdate || isApproveSeller) {
      let formValues = {
        ...data?.vatInfo,
        ...data,
        cityID: data?.ward?.version === "V1" ? null : data?.cityID,
        wardID: data?.ward?.version === "V1" ? null : data?.wardID,
        address:
          data?.ward?.version === "V1"
            ? [data?.address, data?.ward?.name, data?.district?.name, data?.city?.name].filter(Boolean).join(", ")
            : data?.address || "",
        companyAddress: data?.vatInfo?.address,
        company_email: data?.vatInfo?.email,
        legal_representative_phone: data?.vatInfo?.telephone,
        warehouseIDs: data?.warehouses?.map(({ id }) => id),
        contactName: data?.representName,
        contactPhone: data?.representPhone,
        contactEmail: data?.representEmail,
        paymentMethod: data?.paymentMethod || PAYMENT_METHOD.DEBT,
      };
      if (isUpdate && data?.fullName) {
        formValues.fullName = data.fullName.toUpperCase();
      }

      form.setFieldsValue({
        ...formValues,
      });
      setLocation({
        ...location,
        cityID: data?.ward?.version === "V1" ? null : data?.cityID,
      });
    } else {
      const formValues = {
        type: COMPANY_TYPE.COMPANY,
        paymentMethod: PAYMENT_METHOD.DEBT,
        ...history.location.state,
      };

      form.setFieldsValue({
        ...formValues,
      });
    }
  }

  const clearFields = () => {
    form.resetFields();
    setLocation({
      cityID: null,
    });
    setIsCompany(true);
  };

  const handleSubmit = async (isDetail, isRedirect) => {
    const values = await form.validateFields();

    const {
      fullName,
      email,
      address,
      bankAccountNumber,
      bankBranch,
      bankID,
      cityID,
      companyAddress,
      company_email,
      debtAge,
      debtLimit,
      idNumber,
      legal_representative_phone,
      representative,
      sellerLevelID,
      shortName,
      taxIDNumber,
      telephone,
      wardID,
      warehouseIDs,
      type,
      paymentMethod,
      contactName,
      contactEmail,
      contactPhone,
      defaultExportVat,
      allowEditVat,
      salesPersonID,
      salesManID,
    } = values;

    const payload = {
      sellerInfo: {
        idNumber,
        type: type || COMPANY_TYPE.COMPANY,
        logoUrl: logoUrl || data?.logoUrl,
        shortName,
        debtAge: paymentMethod === PAYMENT_METHOD.DEBT ? debtAge : undefined,
        debtLimit: paymentMethod === PAYMENT_METHOD.DEBT ? debtLimit : undefined,
        sellerLevelID,
        fullName,
        email: email || undefined,
        address,
        cityID,
        wardID,
        telephone,
        paymentMethod,
        representName: contactName,
        representEmail: contactEmail,
        representPhone: contactPhone,
        sellerGroupID:
          [data?.sellerGroup?.code, data?.sellerGroup?.name].join(" - ") === values?.sellerGroupID
            ? data?.sellerGroupID
            : values?.sellerGroupID,
        isSupplier: true,
      },
      vatInfo: {
        address: companyAddress,
        bankAccountNumber,
        bankBranch,
        bankID,
        businessName: fullName,
        email: company_email,
        representative,
        taxIDNumber,
        telephone: legal_representative_phone,
        defaultExportVat,
        allowEditVat,
      },
      warehouseIDs,
      saleCategories: [],
      saleID: salesManID,
    };
    if (isDetail) {
      try {
        await handleUpdateSeller({
          sellerID: id,
          ...payload,
        });
        notify.success({
          message: t("seller.updateSellerSuccess"),
        });
        history.goBack();
      } catch (err) {
        notify.error({
          message: t("seller.updateSellerFail"),
          description: err.message,
        });
      }
    } else {
      if (isApproveSeller) {
        try {
          await handleApprovePotentialCustomer({ customerID: potentialCustomerId, request: payload });
          notify.success({
            message: t("seller.approveSellerSuccess"),
          });
          clearFields();
          refetch();
          history.push(`/seller/detail/${id}`);
        } catch (err) {
          notify.error({
            message: t("seller.createSellerFail"),
            description: err.message,
          });
        }
      } else {
        try {
          await handleCreateSeller(payload);
          notify.success({
            message: t("seller.createSellerSuccess"),
          });
          clearFields();
          refetch();
          isRedirect && history.push("/seller");
        } catch (err) {
          notify.error({
            message: t("seller.createSellerFail"),
            description: err.message,
          });
        }
      }
    }
  };

  useEffect(() => {
    initForm();
    let formValues = {
      ...data,
      cityID: data?.ward?.version === "V1" ? null : data?.cityID,
      wardID: data?.ward?.version === "V1" ? null : data?.wardID,
      address:
        data?.ward?.version === "V1"
          ? [data?.address, data?.ward?.name, data?.district?.name, data?.city?.name].filter(Boolean).join(", ")
          : data?.address || "",
      sellerGroupID: data?.sellerGroupID || undefined,
      salesPersonID: data?.sellerGroup?.manager?.id,
      salesManID: data?.salesMan?.id,
    };
    if (isUpdate && data?.fullName) {
      formValues.fullName = data.fullName.toUpperCase();
    }
    form.setFieldsValue(formValues);
    const currentType = form.getFieldValue("type");
    setIsCompany(currentType === COMPANY_TYPE.COMPANY || currentType === COMPANY_TYPE.BUSINESS_HOUSEHOLD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading]);

  return (
    <div className="create-seller-container">
      <Spinner loading={loading} />

      <PageHeader
        pageTitle={
          isUpdate ? (
            <p>
              {t("seller.seller")} <span style={{ textTransform: "capitalize" }}>{data?.shortName}</span>{" "}
              <span className="seller-code">#{data?.code}</span>
            </p>
          ) : (
            t("seller.addSeller")
          )
        }
        routes={[
          {
            path: "/setting",
            name: t("common.systemSetting"),
          },
          {
            path: "/seller",
            name: t("seller.sellerManage"),
          },
          {
            path: "",
            name: isUpdate ? t("seller.updateSeller") : t("seller.addSeller"),
          },
        ]}
      />

      <div className="tabs-frame">
        <Tabs
          tabBarGutter={40}
          defaultActiveKey="common-info"
          tabBarStyle={{
            backgroundColor: "#e7f0ff",
            padding: "12px 24px",
            marginBottom: "20px",
            borderRadius: "8px",
          }}
          activeKey={activeTab}
          onChange={changeTab}
        >
          <TabPane tab={t("seller.commonInfo")} key="common-info">
            <CommonInfo
              defaultLogoUrl={data?.logoUrl}
              getLogoUrl={getLogoUrl}
              form={form}
              isUpdate={isUpdate}
              isApproveSeller={isApproveSeller}
              location={location}
              setLocation={setLocation}
              isCompany={isCompany}
              setIsCompany={setIsCompany}
              sellerData={data}
              sellerGroup={data?.sellerGroup}
              saveSeller={() => handleSubmit(isUpdate, true)}
              saveAndAddSeller={() => handleSubmit(false, false)}
              loadingBtn={loadingCreate || loadingUpdate || loadingApprove}
            />
          </TabPane>
          {/* {isUpdate && canViewContact && (
            <TabPane tab={t('seller.addressList')} key="contact">
              <AddressList />
            </TabPane>
          )} */}
        </Tabs>
      </div>
    </div>
  );
};
